/* eslint-disable react/jsx-no-bind */

import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import { bemGenerator } from '@prisjakt/ui-base';
import { Link } from 'react-router-dom';
import T from '@prisjakt/ui-translate';
import { Checkbox, TextInput } from '@prisjakt/ui-inputs';
import serialize from 'form-serialize';

import SubmitButton from '../components/submit-button';

import InfoPageModal from '../../../components/modal/info-page';

import SocialNetworks from '../components/social-networks';

import './index.scss';

const styles = bemGenerator('signup-page', [
  'spacer',
  'section',
  'form',
  'link',
  'feedback',
  'submit',
  'spinner',
]);

class Signup extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
    this.handleCheckbox = this.handleCheckbox.bind(this);
  }

  getInitialState() {
    return {
      showPassword: false,
      userAgreement: false,
      externalUserAgreement: false,
      externalNewsletter: false,
      newsletter: false,
      capsLock: false,
      inputStatus: '',
      popup: undefined,
    };
  }

  handleChange(e) {
    this.props.clearErrors(e.target.name);
  }

  handleCheckbox(e) {
    this.setState({
      [e.target.name]: e.target.checked,
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    const { email = '', password = '' } = serialize(e.target, { hash: true });
    const { userAgreement, newsletter } = this.state;

    this.props.validate({ email, password, userAgreement }, () => {
      this.props.createUser({
        email,
        password,
        userAgreement,
        newsletter,
      });
    });
  }

  render() {
    const { t, renderMode } = this.context;
    const { errors, pending, infoPageMap } = this.props;
    const {
      showPassword,
      capsLock,
      userAgreement,
      externalUserAgreement,
      externalNewsletter,
      newsletter,
      popup,
    } = this.state;

    const infoPagePopup = infoPageMap[popup];

    const infoPageButton = (infoPage, label) => (
      <button
        type="button"
        key={infoPage}
        className={styles.link}
        onClick={e => {
          e.preventDefault();
          this.setState({
            popup: infoPage,
          });
        }}
      >
        {label}
      </button>
    );

    // Currently, android uses signup page
    // from RFE. We need to remove some element from
    // this view
    const isNative = renderMode === 'native';

    return (
      <div className={styles.block}>
        <InfoPageModal
          isOpen={infoPagePopup !== undefined}
          infoPage={infoPagePopup}
          onRequestClose={() => {
            this.setState({ popup: undefined });
          }}
        />

        <div className="row">
          <div className={`columns small-12 medium-6 ${styles.section}`}>
            <div className={styles.spacer}>
              <div
                className={styles.feedback.withModifier({
                  error: errors.form,
                })}
              >
                {errors.form && t('errors', errors.form[0].code)}
              </div>
              <form
                method="post"
                noValidate
                className={styles.form}
                onSubmit={e => this.handleSubmit(e)}
              >
                <TextInput
                  autoFocus
                  onChange={this.handleChange.bind(this)}
                  name="email"
                  label={t('account', 'input.email')}
                  type="email"
                  tabIndex="0"
                  warning={errors.email}
                  helper={
                    errors.email ? t('errors', errors.email[0].code) : null
                  }
                />

                <div className={styles.spacer}>
                  <TextInput
                    onChange={this.handleChange.bind(this)}
                    onKeyUp={e =>
                      this.setState({
                        capsLock: this.props.isCapsLockOn(e),
                      })
                    }
                    name="password"
                    label={t('account', 'input.password')}
                    type={showPassword ? 'text' : 'password'}
                    tabIndex="0"
                    warning={errors.password}
                    helper={
                      errors.password
                        ? t('errors', errors.password[0].code)
                        : null
                    }
                    capsWarning={capsLock}
                  >
                    <div>
                      <Checkbox
                        name="showPassword"
                        label={t('account', 'input.password-toggle')}
                        onChange={this.handleCheckbox}
                        checked={showPassword}
                        tabIndex="-1"
                      />
                    </div>
                  </TextInput>
                </div>

                <div className={styles.spacer}>
                  <Checkbox
                    name="newsletter"
                    label={t('account', 'signup.newsletter-info')}
                    onChange={this.handleCheckbox}
                    checked={newsletter}
                  />
                </div>

                <div className={styles.spacer}>
                  <Checkbox
                    name="userAgreement"
                    onChange={this.handleCheckbox}
                    checked={userAgreement}
                    warning={errors.userAgreement}
                    helper={
                      errors.userAgreement
                        ? t('errors', errors.userAgreement[0].code)
                        : null
                    }
                  >
                    <T
                      domain="account"
                      termsConditionsLink={infoPageButton(
                        'userAgreement',
                        t('account', 'signup.terms-conditions')
                      )}
                      privacyPolicyLink={infoPageButton(
                        'privacyPolicy',
                        t('account', 'signup.privacy-policy')
                      )}
                    >
                      signup.terms-accept
                    </T>
                  </Checkbox>
                </div>

                <div className={styles.spacer}>
                  <SubmitButton
                    color="primary"
                    style={{ width: '100%' }}
                    loading={pending}
                    text={t('account', 'signup.submit-button')}
                  />
                </div>
              </form>
            </div>
            {!isNative && (
              <div className={styles.spacer}>
                <p>
                  <T domain="account">signup.login-link</T>
                  &nbsp;
                  <Link to="/login" className={styles.link}>
                    {t('account', 'login.title')}
                  </Link>
                </p>
              </div>
            )}
          </div>

          {!isNative && this.context.market.socialNetworks.length > 0 && (
            <div className={`columns small-12 medium-6 ${styles.section}`}>
              <div className={styles.spacer}>
                <h4>
                  <T domain="account">signup.external-title</T>
                </h4>
              </div>

              <div className={styles.spacer}>
                <Checkbox
                  name="externalNewsletter"
                  label={t('account', 'signup.newsletter-info')}
                  onChange={this.handleCheckbox}
                  checked={externalNewsletter}
                />
              </div>

              <div className={styles.spacer}>
                <Checkbox
                  name="externalUserAgreement"
                  onChange={this.handleCheckbox}
                  checked={externalUserAgreement}
                  warning={
                    errors.socialNetworks &&
                    errors.socialNetworks[0] &&
                    errors.socialNetworks[0].code ===
                      'external.user-terms-not-accepted'
                  }
                  helper={
                    errors.socialNetworks &&
                    errors.socialNetworks[0] &&
                    errors.socialNetworks[0].code ===
                      'external.user-terms-not-accepted'
                      ? t('errors', 'validation.user-agreement')
                      : null
                  }
                >
                  <T
                    domain="account"
                    termsConditionsLink={infoPageButton(
                      'userAgreement',
                      t('account', 'signup.terms-conditions')
                    )}
                    privacyPolicyLink={infoPageButton(
                      'privacyPolicy',
                      t('account', 'signup.privacy-policy')
                    )}
                  >
                    signup.terms-accept
                  </T>
                </Checkbox>
              </div>

              <div className={styles.spacer}>
                <div className={styles.form}>
                  <SocialNetworks
                    action="signup"
                    styles={styles}
                    onAuthResponse={this.props.onSocialResponse}
                    onAuthInit={this.props.onSocialInit}
                    createAccount
                    acceptTerms={externalUserAgreement}
                    newsLetter={externalNewsletter}
                  />
                </div>
              </div>
              {errors.socialNetworks &&
              errors.socialNetworks.filter(
                e => !['external.user-terms-not-accepted'].includes(e.code)
              ).length ? (
                <div
                  className={styles.feedback.withModifier({
                    margin: true,
                    error: errors.socialNetworks,
                  })}
                >
                  {t('errors', errors.socialNetworks[0].code)}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const { func, object, bool } = PropTypes;

Signup.propTypes = {
  infoPageMap: object.isRequired, // eslint-disable-line react/forbid-prop-types
  validate: func.isRequired,
  createUser: func.isRequired,
  pending: bool.isRequired,
  errors: object.isRequired, // eslint-disable-line react/forbid-prop-types
  isCapsLockOn: func.isRequired,
  onSocialInit: func.isRequired,
  onSocialResponse: func.isRequired,
  clearErrors: func.isRequired,
};

Signup.contextType = AppContext;

export default Signup;
